import { Chart } from "@/ui/organisms/Chart/Chart";
import { GroupedCategoryAxis, MoneyAxis } from "@/ui/organisms/Chart/Axis";
import {
    filterNullValuesFromChartData,
    formatCurrency,
} from "./utils/filterChartData";
import { aggregateChartData, PeriodType } from "./utils/periodAggregation";
import { usePeriodFilter } from "./hooks/usePeriodFilter";

export function AssetProgression({
    isCondensed,
    data,
}: {
    isCondensed?: boolean;
    data?: any;
}) {
    const { selectedPeriod, periodFilterProps } = usePeriodFilter("monthly");

    const aggregatedData = aggregateChartData(
        data || [],
        selectedPeriod.value as PeriodType,
        "assetProgression",
    );

    const filteredData = filterNullValuesFromChartData(aggregatedData);

    if (filteredData.length === 0) {
        return null;
    }

    return (
        <Chart
            title="Asset Progression"
            description="The company maintains strong liquidity and asset quality, with liquid assets consistently comprising a significant and growing portion of total assets. However, periods of negative or volatile swings in receivables and inventory point to potential operational inefficiencies or risks that warrant further investigation."
            isCondensed={isCondensed}
            periodFilter={periodFilterProps}
            options={{
                data: filteredData,
                tooltip: {
                    mode: "single",
                },
                series: [
                    {
                        type: "line",
                        xKey: "month",
                        yKey: "totalAssets",
                        yName: "Total Assets",
                        marker: {
                            enabled: false,
                        },
                        tooltip: {
                            renderer: (params) => {
                                const datum = params.datum;
                                const month = `${datum.month[1]} ${datum.month[0]}`;

                                return {
                                    heading: `${month}`,
                                    title: "Total Assets (Financial Statements)",
                                    data: [
                                        {
                                            label: "Total Assets (Financial Statements)",
                                            value: formatCurrency(
                                                datum.totalAssets,
                                            ),
                                        },
                                        {
                                            label: "Current Assets (Financial Statements)",
                                            value: formatCurrency(
                                                datum.currentAssets,
                                            ),
                                        },
                                        {
                                            label: "Total Non-Current Assets (Financial Statements)",
                                            value: formatCurrency(
                                                datum.totalNoncurrentAssets,
                                            ),
                                        },
                                    ],
                                };
                            },
                        },
                    },
                    {
                        type: "line",
                        xKey: "month",
                        yKey: "liquidAssets",
                        yName: "Liquid Assets",
                        marker: {
                            enabled: false,
                        },
                        tooltip: {
                            renderer: (params) => {
                                const datum = params.datum;
                                const month = `${datum.month[1]} ${datum.month[0]}`;

                                return {
                                    heading: `${month}`,
                                    title: "Liquid Assets (Financial Statements)",
                                    data: [
                                        {
                                            label: "Liquid Assets (Financial Statements)",
                                            value: formatCurrency(
                                                datum.liquidAssets,
                                            ),
                                        },
                                        {
                                            label: "Cash (Financial Statements)",
                                            value: formatCurrency(datum.cash),
                                        },
                                        {
                                            label: "Total Receivables (Financial Statements)",
                                            value: formatCurrency(
                                                datum.totalReceivables,
                                            ),
                                        },
                                        {
                                            label: "Total Inventory (Financial Statements)",
                                            value: formatCurrency(
                                                datum.totalInventory,
                                            ),
                                        },
                                        {
                                            label: "Fixed Assets (Financial Statements)",
                                            value: formatCurrency(
                                                datum.noncurrentAssetsPropertyPlantAndEquipment,
                                            ),
                                        },
                                    ],
                                };
                            },
                        },
                    },
                ],
                axes: [
                    GroupedCategoryAxis.doubleDepth,
                    {
                        ...MoneyAxis,
                        keys: ["totalAssets", "liquidAssets"],
                    },
                ],
            }}
        />
    );
}
